import { useEffect, useRef, useState } from "react";

/**
 * Long-press drag-and-drop list for the driver app.
 * - Supports BOTH touch (mobile) and mouse (desktop/tablet) inputs
 * - Long-press of 150ms before drag activates (so taps still pass through)
 * - Visual ghost while dragging + orange border-top indicator on drop target
 */
export function DraggableList({ items, onReorder, renderItem }) {
  const listRef = useRef(null);
  const ghostRef = useRef(null);
  const drag = useRef(null);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const getClientY = (e) => {
      if (e.touches && e.touches[0]) return e.touches[0].clientY;
      return e.clientY;
    };

    const onMove = (e) => {
      const d = drag.current;
      if (!d || !d.active) return;
      e.preventDefault();
      const cy = getClientY(e);
      const newTop = cy - d.offsetY;

      if (ghostRef.current) {
        ghostRef.current.style.top = newTop + "px";
      }

      const children = Array.from(el.children);
      let target = d.fromIdx;
      for (let i = 0; i < children.length; i++) {
        const rect = children[i].getBoundingClientRect();
        if (cy > rect.top + rect.height / 2) target = i;
      }
      if (target !== d.targetIdx) {
        d.targetIdx = target;
        children.forEach((child, i) => {
          child.style.borderTop = (i === target && target !== d.fromIdx) ? "3px solid #FF5200" : "";
          child.style.opacity = (i === d.fromIdx) ? "0.3" : "1";
        });
      }
    };

    const onEnd = () => {
      const d = drag.current;
      if (!d) return;
      clearTimeout(d.timer);
      document.body.style.overflow = "";

      if (d.active && d.fromIdx !== d.targetIdx) {
        onReorder(d.fromIdx, d.targetIdx);
      }

      if (ghostRef.current) {
        ghostRef.current.style.display = "none";
      }
      const children = Array.from(el.children);
      children.forEach((child) => {
        child.style.borderTop = "";
        child.style.opacity = "1";
      });

      drag.current = null;
      forceUpdate((v) => v + 1);
    };

    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    // Mouse listeners attached to document to keep working when cursor leaves the list
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onEnd);
    return () => {
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onEnd);
    };
  }, [items, onReorder]);

  const startDrag = (idx, clientY) => {
    const el = listRef.current?.children[idx];
    if (!el) return;
    const rect = el.getBoundingClientRect();

    drag.current = {
      fromIdx: idx,
      targetIdx: idx,
      offsetY: clientY - rect.top,
      active: false,
      timer: setTimeout(() => {
        if (!drag.current) return;
        drag.current.active = true;
        document.body.style.overflow = "hidden";

        if (ghostRef.current) {
          ghostRef.current.style.display = "block";
          ghostRef.current.style.left = rect.left + "px";
          ghostRef.current.style.top = rect.top + "px";
          ghostRef.current.style.width = rect.width + "px";
          ghostRef.current.style.height = rect.height + "px";
        }

        el.style.opacity = "0.3";
        forceUpdate((v) => v + 1);
      }, 150),
    };
  };

  const handleTouchStart = (idx, e) => {
    const touch = e.touches[0];
    startDrag(idx, touch.clientY);
  };

  const handleMouseDown = (idx, e) => {
    // Only left-click
    if (e.button !== 0) return;
    e.preventDefault();
    startDrag(idx, e.clientY);
  };

  return (
    <>
      <div ref={listRef} className="space-y-2">
        {items.map((item, idx) => (
          <div key={item.id}>
            {renderItem(item, idx, (e) => {
              if (e.touches) handleTouchStart(idx, e);
              else handleMouseDown(idx, e);
            })}
          </div>
        ))}
      </div>
      <div
        ref={ghostRef}
        style={{ display: "none", position: "fixed", zIndex: 9999, pointerEvents: "none", opacity: 0.92, boxShadow: "0 8px 32px rgba(0,0,0,0.18)", transition: "none" }}
      >
        {drag.current?.active && items[drag.current.fromIdx] && renderItem(items[drag.current.fromIdx], drag.current.fromIdx, null)}
      </div>
    </>
  );
}
